import React from 'react';
import { Translation } from '../../commonTypes/translation.types';
import { TranslationFormatter } from './TranslationFormatter';
import { NotificationUserService } from './NotificationUserService';
import { NotificationClientService } from './NotificationClientService';
import { Notification, NotificationTitleEnum } from '../../commonTypes/NotificationTypes/notification.types';
import {
    BranchChangeNotification,
    LeaveRequestApproveNotification,
    LeaveRequestDeleteNotification,
    LeaveRequestRejectNotification,
    LeaveRequestUpdateOnGoingNotification,
    PolicyDeletion,
    PolicyMembersChanged,
    PolicyChangeNotification,
    RoleChangeNotification,
    UploadDocumentNotification,
    LeaveRequestCreationNotification,
    LoyaltyProgramBonusNotification,
    PolicyNameUpdatedNotification,
    MedicalLeaveCreationNotification,
    MedicalLeaveUpdateNotification,
    MedicalLeaveRequestApproveUpdate,
    MedicalLeaveRequestRejecteUpdate,
    MedicalLeaveRequestEnded,
    MedicalLeaveUpdatedNotification,
} from '../../commonTypes/NotificationTypes/userService.types';
import { NotificationOrganizationService } from './NotificationOrganizationService';
import {
    TeamLeadChangeNotification,
    TeamLeadUpdateNotification,
    TeamMemberRemoveNotification,
    TeamMembersAddNotification,
} from '../../commonTypes/NotificationTypes/organizationService.types';
import { AddAssigneeNotification, RemoveAssigneeNotification } from '../../commonTypes/NotificationTypes/project.types';
import { NotificationProjectService } from './NotificationProjectService';
import { NotificationTimelogionService } from './NotificationTimelogService';
import { SubmissionIntervalNotification } from '../../commonTypes/NotificationTypes/timelog.types';
import {
    EvaluationUpdateRequestedNotification,
    ApproveRejectUpdateNotification,
    RequestedEvaluationNotification,
    PerformedEvaluationNotification,
} from '../../commonTypes/NotificationTypes/evaluationService.types';
import { InvoiceUpdatedNotification } from '../../commonTypes/NotificationTypes/clientService.types';

export class NotificationFormatter {
    private notificationUserSub: string;
    private notificationUserService: NotificationUserService;
    private notificationOrganizationService: NotificationOrganizationService;
    private translationFormatter: TranslationFormatter;
    private notificationProjectService: NotificationProjectService;
    private notificationTimelogService: NotificationTimelogionService;
    private notificationClientService: NotificationClientService;

    constructor(notificationUserSub: string, data?: Translation) {
        this.translationFormatter = new TranslationFormatter(data);
        this.notificationUserService = new NotificationUserService(this.translationFormatter);
        this.notificationOrganizationService = new NotificationOrganizationService(this.translationFormatter);
        this.notificationProjectService = new NotificationProjectService(this.translationFormatter);
        this.notificationTimelogService = new NotificationTimelogionService(this.translationFormatter);
        this.notificationClientService = new NotificationClientService(this.translationFormatter);
        this.notificationUserSub = notificationUserSub;
    }

    private generators = {
        [NotificationTitleEnum.LEAVE_REQUEST_APPROVE]: (notification: LeaveRequestApproveNotification) =>
            this.notificationUserService.generateLeaveRequestApprovementMessage(notification, this.notificationUserSub),
        [NotificationTitleEnum.LEAVE_REQUEST_REJECT]: (notification: LeaveRequestRejectNotification) =>
            this.notificationUserService.generateLeaveRequestRejectionMessage(notification, this.notificationUserSub),
        [NotificationTitleEnum.LEAVE_REQUEST_DELETION]: (notification: LeaveRequestDeleteNotification) =>
            this.notificationUserService.generateLeaveRequestDeletionMessage(notification),
        [NotificationTitleEnum.LEAVE_REQUEST_UPDATE_ONGOING]: (notification: LeaveRequestUpdateOnGoingNotification) =>
            this.notificationUserService.generateLeaveRequestUpdateOnGoingMessage(notification),
        [NotificationTitleEnum.POLICY_DELETION]: (notification: PolicyDeletion) =>
            this.notificationUserService.generatePolicyDeletionMessage(notification),
        [NotificationTitleEnum.POLICY_MEMBERS_CHANGED]: (notification: PolicyMembersChanged) =>
            this.notificationUserService.generatePolicyMembersChangeMessage(notification),
        [NotificationTitleEnum.POLICY_CHANGE]: (notification: PolicyChangeNotification) =>
            this.notificationUserService.generatePolicyChangeMessage(notification),
        [NotificationTitleEnum.BRANCH_CHANGE]: (notification: BranchChangeNotification) =>
            this.notificationUserService.generateBranchChangeMessage(notification),
        [NotificationTitleEnum.ROLE_CHANGE]: (notification: RoleChangeNotification) =>
            this.notificationUserService.generateRoleChangeMessage(notification),
        [NotificationTitleEnum.DOCUMENT_UPLOAD]: (notification: UploadDocumentNotification) =>
            this.notificationUserService.generateDocumentUploadMessage(notification),
        [NotificationTitleEnum.TEAM_MEMBERS_ADD]: (notification: TeamMembersAddNotification) =>
            this.notificationOrganizationService.generateAddTeamMemberMessage(notification),
        [NotificationTitleEnum.TEAM_MEMBER_REMOVE]: (notification: TeamMemberRemoveNotification) =>
            this.notificationOrganizationService.generateRemoveTeamMemberMessage(notification),
        [NotificationTitleEnum.TEAM_LEAD_UPDATE]: (notification: TeamLeadUpdateNotification) =>
            this.notificationOrganizationService.generateTeamLeadUpdateMessage(notification),
        [NotificationTitleEnum.CHANGE_TEAM_LEAD]: (notification: TeamLeadChangeNotification) =>
            this.notificationOrganizationService.generateTeamLeadChangeMessage(notification),
        [NotificationTitleEnum.ADD_ASSIGNEE]: (notification: AddAssigneeNotification) =>
            this.notificationProjectService.generateAddAssigneeMessage(notification),
        [NotificationTitleEnum.REMOVE_ASSIGNEE]: (nottification: RemoveAssigneeNotification) =>
            this.notificationProjectService.generateRemoveAssigneeMessage(nottification),
        [NotificationTitleEnum.SUBMISSION_INTERVAL]: (notification: SubmissionIntervalNotification) =>
            this.notificationTimelogService.generateSubmissionIntervalMessage(notification),
        [NotificationTitleEnum.LEAVE_REQUEST_CREATION]: (notification: LeaveRequestCreationNotification) =>
            this.notificationUserService.generateLeaveRequestCreationMessage(notification),
        [NotificationTitleEnum.LOYALTY_PROGRAM_BONUS]: (notification: LoyaltyProgramBonusNotification) =>
            this.notificationUserService.generateLoyaltyProgramBonusMessage(notification, this.notificationUserSub),
        [NotificationTitleEnum.POLICY_NAME_UPDATED]: (notification: PolicyNameUpdatedNotification) =>
            this.notificationUserService.generatePolicyNameUpdatedMessage(notification),
        [NotificationTitleEnum.MEDICAL_LEAVE_REQUEST_CREATION]: (notification: MedicalLeaveCreationNotification) =>
            this.notificationUserService.generateMedicalLeaveRequestCreationMessage(notification),
        [NotificationTitleEnum.MEDICAL_LEAVE_UPDATE]: (notification: MedicalLeaveUpdateNotification) =>
            this.notificationOrganizationService.generateMedicalLeaveUpdateMessage(notification),
        [NotificationTitleEnum.MEDICAL_LEAVE_REQUEST_APPROVE_UPDATE]: (notification: MedicalLeaveRequestApproveUpdate) =>
            this.notificationUserService.generateMedicalLeaveRequestApprovementMessage(notification, this.notificationUserSub),
        [NotificationTitleEnum.MEDICAL_LEAVE_REQUEST_REJECT_UPDATE]: (notification: MedicalLeaveRequestRejecteUpdate) =>
            this.notificationUserService.generateMedicalLeaveRequestRejectionMessage(notification, this.notificationUserSub),
        [NotificationTitleEnum.MEDICAL_LEAVE_REQUEST_ENDED]: (notification: MedicalLeaveRequestEnded) =>
            this.notificationUserService.generateMedicalLeaveRequestEndedMessage(notification),
        [NotificationTitleEnum.MEDICAL_LEAVE_REQUEST_UPDATED]: (notification: MedicalLeaveUpdatedNotification) =>
            this.notificationUserService.generateMedicalLeaveRequestUpdatedMessage(notification),
        [NotificationTitleEnum.EVALUATION_UPDATE_REQUESTED]: (notification: EvaluationUpdateRequestedNotification) =>
            this.notificationUserService.generateEvaluationUpdateRequestedMessage(notification),
        [NotificationTitleEnum.APPROVE_REJECT_UPDATE]: (notification: ApproveRejectUpdateNotification) =>
            this.notificationUserService.generateApproveRejectUpdateMessage(notification, this.notificationUserSub),
        [NotificationTitleEnum.REQUESTED_EVALUATION]: (notification: RequestedEvaluationNotification) =>
            this.notificationUserService.generateEvaluationRequestedEvaluationMessage(notification, this.notificationUserSub),
        [NotificationTitleEnum.PERFORMED_EVALUATION]: (notification: PerformedEvaluationNotification) =>
            this.notificationUserService.generateEvaluationPerformedEvaluationMessage(notification),
        [NotificationTitleEnum.INVOICE_UPDATED]: (notification: InvoiceUpdatedNotification) =>
            this.notificationClientService.generateInvoiceUpdatedMessage(notification, this.notificationUserSub),
    } as const;

    private icons = {
        [NotificationTitleEnum.LEAVE_REQUEST_APPROVE]: '/svg/notifications/success.svg',
        [NotificationTitleEnum.LEAVE_REQUEST_REJECT]: '/svg/notifications/error.svg',
        [NotificationTitleEnum.LEAVE_REQUEST_DELETION]: '/svg/notifications/warning.svg',
        [NotificationTitleEnum.LEAVE_REQUEST_UPDATE_ONGOING]: '/svg/notifications/warning.svg',
        [NotificationTitleEnum.POLICY_DELETION]: '/svg/notifications/success.svg',
        [NotificationTitleEnum.POLICY_MEMBERS_CHANGED]: '/svg/notifications/success.svg',
        [NotificationTitleEnum.POLICY_CHANGE]: '/svg/notifications/success.svg',
        [NotificationTitleEnum.BRANCH_CHANGE]: '/svg/notifications/success.svg',
        [NotificationTitleEnum.DOCUMENT_UPLOAD]: '/svg/notifications/success.svg',
        [NotificationTitleEnum.ROLE_CHANGE]: '/svg/notifications/success.svg',
        [NotificationTitleEnum.TEAM_MEMBERS_ADD]: '/svg/notifications/success.svg',
        [NotificationTitleEnum.TEAM_MEMBER_REMOVE]: '/svg/notifications/success.svg',
        [NotificationTitleEnum.CHANGE_TEAM_LEAD]: '/svg/notifications/success.svg',
        [NotificationTitleEnum.TEAM_LEAD_UPDATE]: '/svg/notifications/success.svg',
        [NotificationTitleEnum.ADD_ASSIGNEE]: '/svg/notifications/success.svg',
        [NotificationTitleEnum.REMOVE_ASSIGNEE]: '/svg/notifications/success.svg',
        [NotificationTitleEnum.SUBMISSION_INTERVAL]: '/svg/notifications/success.svg',
        [NotificationTitleEnum.LEAVE_REQUEST_CREATION]: '/svg/notifications/warning.svg',
        [NotificationTitleEnum.LOYALTY_PROGRAM_BONUS]: '/svg/notifications/success.svg',
        [NotificationTitleEnum.POLICY_NAME_UPDATED]: '/svg/notifications/success.svg',
        [NotificationTitleEnum.MEDICAL_LEAVE_REQUEST_CREATION]: '/svg/notifications/warning.svg',
        [NotificationTitleEnum.MEDICAL_LEAVE_UPDATE]: 'svg/notifications/success.svg',
        [NotificationTitleEnum.MEDICAL_LEAVE_REQUEST_APPROVE_UPDATE]: '/svg/notifications/success.svg',
        [NotificationTitleEnum.MEDICAL_LEAVE_REQUEST_REJECT_UPDATE]: '/svg/notifications/error.svg',
        [NotificationTitleEnum.MEDICAL_LEAVE_REQUEST_ENDED]: '/svg/notifications/warning.svg',
        [NotificationTitleEnum.MEDICAL_LEAVE_REQUEST_UPDATED]: '/svg/notifications/success.svg',
        [NotificationTitleEnum.EVALUATION_UPDATE_REQUESTED]: '/svg/notifications/warning.svg',
        [NotificationTitleEnum.APPROVE_REJECT_UPDATE]: '/svg/notifications/success.svg',
        [NotificationTitleEnum.INVOICE_UPDATED]: '/svg/notifications/warning.svg',
        [NotificationTitleEnum.REQUESTED_EVALUATION]: '/svg/notifications/success.svg',
        [NotificationTitleEnum.PERFORMED_EVALUATION]: '/svg/notifications/success.svg',
    } as const;

    public generateMessage(notification: Notification): React.ReactNode {
        const generator = this.generators[notification.title as keyof typeof this.generators];
        if (generator) {
            return generator(notification as any);
        }
        return React.createElement('span', null, this.translationFormatter.getTranslation('notificationMessages.unknownNotification'));
    }

    public generateTitle(title: string): string {
        const key = `notifications.${title}` as keyof Translation;
        return this.translationFormatter.getTranslation(key, { title: title });
    }

    public getIcon(title: NotificationTitleEnum): string {
        return this.icons[title] || '/svg/notifications/default.svg';
    }
}
